import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, X, ImagePlus, Trash2, Upload } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { useI18n } from "../../i18n/I18nContext";
import { api, formatApiError } from "../../lib/api";

const EMPTY = {
    title_fr: "",
    title_en: "",
    description_fr: "",
    description_en: "",
    type: "villa",
    transaction: "sale",
    price: 0,
    currency: "FCFA",
    city: "Bamako",
    neighborhood: "",
    surface: "",
    bedrooms: "",
    bathrooms: "",
    images: [],
    featured: false,
    available: true,
};

export default function AdminPropertyForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const { t } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [imgInput, setImgInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/properties/${id}`)
            .then((r) => {
                const d = r.data;
                setForm({
                    ...EMPTY,
                    ...d,
                    surface: d.surface ?? "",
                    bedrooms: d.bedrooms ?? "",
                    bathrooms: d.bathrooms ?? "",
                });
            })
            .catch(() => toast.error("Bien introuvable"));
    }, [id, isEdit]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const addImage = () => {
        const url = imgInput.trim();
        if (!url) return;
        setForm((f) => ({ ...f, images: [...f.images, url] }));
        setImgInput("");
    };

    const removeImage = (idx) => {
        setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
    };

    const uploadFiles = async (files) => {
        const list = Array.from(files || []);
        if (list.length === 0) return;
        setUploading(true);
        try {
            for (const file of list) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/uploads/images", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setForm((f) => ({ ...f, images: [...f.images, data.url] }));
            }
            toast.success(t("admin.saved"));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                ...form,
                price: Number(form.price) || 0,
                surface: form.surface === "" ? null : Number(form.surface),
                bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
                bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
            };
            if (isEdit) {
                await api.put(`/admin/properties/${id}`, payload);
            } else {
                await api.post("/admin/properties", payload);
            }
            toast.success(t("admin.saved"));
            navigate("/admin");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AdminLayout>
            <div className="p-6 lg:p-10 max-w-5xl">
                <div className="mb-8">
                    <div className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-gold mb-2">
                        {t("admin.dashboard")}
                    </div>
                    <h1 className="font-display text-3xl text-brand-navy">
                        {isEdit ? t("admin.edit_property") : t("admin.new_property")}
                    </h1>
                </div>

                <form onSubmit={submit} className="bg-white border border-gray-100 p-6 lg:p-8 space-y-6" data-testid="admin-property-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Titre (FR)" value={form.title_fr} onChange={(v) => setField("title_fr", v)} required testid="form-title-fr" />
                        <Field label="Title (EN)" value={form.title_en} onChange={(v) => setField("title_en", v)} required testid="form-title-en" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Description (FR)" value={form.description_fr} onChange={(v) => setField("description_fr", v)} textarea testid="form-desc-fr" />
                        <Field label="Description (EN)" value={form.description_en} onChange={(v) => setField("description_en", v)} textarea testid="form-desc-en" />
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        <Select label="Type" value={form.type} onChange={(v) => setField("type", v)} testid="form-type"
                            options={[
                                { v: "villa", l: t("properties.filters.villa") },
                                { v: "appartement", l: t("properties.filters.appartement") },
                                { v: "maison", l: t("properties.filters.maison") },
                                { v: "terrain", l: t("properties.filters.terrain") },
                            ]}
                        />
                        <Select label="Transaction" value={form.transaction} onChange={(v) => setField("transaction", v)} testid="form-transaction"
                            options={[
                                { v: "sale", l: t("properties.filters.sale") },
                                { v: "rent", l: t("properties.filters.rent") },
                            ]}
                        />
                        <Field label="Prix" type="number" value={form.price} onChange={(v) => setField("price", v)} required testid="form-price" />
                        <Field label="Devise" value={form.currency} onChange={(v) => setField("currency", v)} testid="form-currency" />
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
                        <Field label="Ville" value={form.city} onChange={(v) => setField("city", v)} required testid="form-city" />
                        <Field label="Quartier" value={form.neighborhood} onChange={(v) => setField("neighborhood", v)} testid="form-neighborhood" />
                        <Field label="Surface (m²)" type="number" value={form.surface} onChange={(v) => setField("surface", v)} testid="form-surface" />
                        <Field label="Chambres" type="number" value={form.bedrooms} onChange={(v) => setField("bedrooms", v)} testid="form-bedrooms" />
                        <Field label="SdB" type="number" value={form.bathrooms} onChange={(v) => setField("bathrooms", v)} testid="form-bathrooms" />
                    </div>

                    <div>
                        <div className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-2">
                            {t("admin.upload_photo")}
                        </div>
                        <div className="mb-3">
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => uploadFiles(e.target.files)}
                                data-testid="form-image-upload"
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                data-testid="form-image-upload-btn"
                                className="inline-flex items-center gap-2 px-5 py-3 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.7rem] tracking-[0.22em] uppercase font-semibold"
                            >
                                <Upload className="w-4 h-4" /> {uploading ? t("admin.saving") : t("admin.upload_photo")}
                            </button>
                            <p className="text-xs text-gray-400 mt-2">{t("admin.upload_hint")}</p>
                        </div>
                        <div className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-2">
                            {t("admin.image_url_label")}
                        </div>
                        <div className="flex gap-2">
                            <input
                                value={imgInput}
                                onChange={(e) => setImgInput(e.target.value)}
                                placeholder="https://..."
                                data-testid="form-image-input"
                                className="flex-1 border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-gold"
                            />
                            <button
                                type="button"
                                onClick={addImage}
                                data-testid="form-image-add"
                                className="inline-flex items-center gap-2 px-5 py-3 bg-brand-navy hover:bg-brand-navy-soft text-white text-[0.7rem] tracking-[0.22em] uppercase font-semibold"
                            >
                                <ImagePlus className="w-4 h-4" /> Ajouter
                            </button>
                        </div>
                        {form.images.length > 0 && (
                            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {form.images.map((src, i) => (
                                    <div key={i} className="relative group border border-gray-100">
                                        <img src={src} alt="" className="w-full aspect-[4/3] object-cover" />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(i)}
                                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white w-7 h-7 inline-flex items-center justify-center"
                                            data-testid={`form-image-remove-${i}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-6">
                        <Toggle label={t("admin.featured")} value={form.featured} onChange={(v) => setField("featured", v)} testid="form-featured" />
                        <Toggle label={t("admin.available")} value={form.available} onChange={(v) => setField("available", v)} testid="form-available" />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="form-submit"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                        >
                            <Save className="w-4 h-4" /> {busy ? "..." : t("admin.save")}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/admin")}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-brand-navy text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                            data-testid="form-cancel"
                        >
                            <X className="w-4 h-4" /> {t("admin.cancel")}
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
}

function Field({ label, value, onChange, required, type = "text", textarea, testid }) {
    const cls = "w-full bg-white border border-gray-200 px-4 py-3 text-sm text-brand-navy focus:outline-none focus:border-brand-gold transition-colors";
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy">
                {label}{required ? <span className="text-brand-gold"> *</span> : null}
            </span>
            <div className="mt-1.5">
                {textarea ? (
                    <textarea
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        rows={4}
                        required={required}
                        data-testid={testid}
                        className={cls + " resize-y min-h-[110px]"}
                    />
                ) : (
                    <input
                        type={type}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        required={required}
                        data-testid={testid}
                        className={cls}
                    />
                )}
            </div>
        </label>
    );
}

function Select({ label, value, onChange, options, testid }) {
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full mt-1.5 bg-white border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-gold"
            >
                {options.map((o) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                ))}
            </select>
        </label>
    );
}

function Toggle({ label, value, onChange, testid }) {
    return (
        <label className="inline-flex items-center gap-3 cursor-pointer select-none">
            <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                data-testid={testid}
                className="w-4 h-4 accent-brand-gold"
            />
            <span className="text-[0.72rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{label}</span>
        </label>
    );
}
